import { useCallback, useMemo, useRef, useState, type ReactElement } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  nextSortState,
  sortRows,
  type SortAccessors,
  type SortDir,
  type SortState,
} from '@jr2os/shared'
import ColumnSortTh from '../components/ui/ColumnSortTh'

/**
 * Orden por columna de una tabla. La forma de usarlo en TODAS las tablas de la aplicación:
 *
 *   const { rows, header } = useTableSort(items, {
 *     name: (r) => r.name,                // por qué valor se ordena (no tiene por qué ser lo que se pinta)
 *     amount: (r) => r.amountCents,       // número → numérico; fecha ISO o Date → cronológico; texto → alfabético
 *   })
 *   …
 *   <thead><tr>{header('name', t('table.name'))}{header('amount', t('table.amount'), { align: 'right' })}<th/></tr></thead>
 *   <tbody>{rows.map(…)}</tbody>
 *
 * Tres estados por columna (sin orden → ascendente → descendente → sin orden = el orden de siempre de la tabla). Lo vacío va
 * siempre al final. Las reglas están en `@jr2os/shared` (`table-sort`), con su smoke.
 *
 * Dónde se guarda el orden:
 *  - `persist: 'url'` (por defecto): en la URL (`?sort=name&dir=desc`), así sobrevive a recargar y se puede compartir. Si la
 *    página tiene VARIAS tablas, cada una lleva su `id` (`?sort_<id>=…&dir_<id>=…`). ☠️ La URL se actualiza con `replace` y
 *    conservando el resto de parámetros (filtros, pestañas).
 *  - `persist: 'state'`: solo en memoria (modales y paneles, donde la URL no pinta nada).
 *
 * `useTableSort` es para listas que se cargan ENTERAS en el navegador. Una tabla paginada en el servidor se ordena en el
 * servidor (el orden de una sola página no es el orden de la lista): ahí se usa `useServerTableSort`, que lleva el mismo
 * estado (columna, sentido, URL, cabeceras) pero NO ordena nada: devuelve `sortParams` para añadir a la petición.
 */
export interface TableSortOptions {
  persist?: 'url' | 'state'
  /** Obligatorio si hay más de una tabla ordenable en la misma página con `persist: 'url'`. */
  id?: string
  /**
   * Si una columna ordena por algo que NO viene en las filas (un nombre buscado en otra lista, un mapa que carga después),
   * pasar aquí eso de lo que depende para que se reordene al cambiar. Siempre con la misma longitud.
   */
  deps?: readonly unknown[]
}

export interface HeaderOptions {
  align?: 'left' | 'right' | 'center'
  /** Relleno y tipografía de la celda de cabecera, si no son los de la cabecera por defecto. */
  className?: string
  /** `false` = sin mayúsculas ni espaciado ancho (la tabla de Proveedores). Por defecto, mayúsculas. */
  caps?: boolean
}

/** El estado del orden (columna y sentido) y cómo cambia, en la URL o en memoria. */
function useSortState(options: TableSortOptions): {
  sort: SortState
  onSort: (key: string) => void
} {
  const persist = options.persist ?? 'url'
  const suffix = options.id ? `_${options.id}` : ''
  const sortParam = `sort${suffix}`
  const dirParam = `dir${suffix}`

  const [searchParams, setSearchParams] = useSearchParams()
  const [local, setLocal] = useState<SortState>({ key: null, dir: 'asc' })

  const urlKey = searchParams.get(sortParam)
  const urlDir: SortDir = searchParams.get(dirParam) === 'desc' ? 'desc' : 'asc'
  const sort: SortState =
    persist === 'url' ? { key: urlKey && urlKey.length > 0 ? urlKey : null, dir: urlDir } : local

  const onSort = useCallback(
    (key: string) => {
      if (persist === 'state') {
        setLocal((cur) => nextSortState(cur, key))
        return
      }
      // ☠️ Se parte de la URL REAL en el momento del clic (`window.location`), no del `searchParams` del último pintado: la forma
      // funcional de setSearchParams de React Router NO encola (usa el del pintado), y un doble clic rápido pisaría el primero.
      const prev = new URLSearchParams(window.location.search)
      const cur: SortState = {
        key: prev.get(sortParam) || null,
        dir: prev.get(dirParam) === 'desc' ? 'desc' : 'asc',
      }
      const next = nextSortState(cur, key)
      const out = new URLSearchParams(prev)
      if (next.key) {
        out.set(sortParam, next.key)
        out.set(dirParam, next.dir)
      } else {
        out.delete(sortParam)
        out.delete(dirParam)
      }
      setSearchParams(out, { replace: true })
    },
    [persist, setSearchParams, sortParam, dirParam]
  )

  return { sort, onSort }
}

/** Fabrica la función `header(key, label, opts)` que pinta la cabecera ordenable de cada columna. */
function useHeader(sort: SortState, onSort: (key: string) => void) {
  const { t } = useTranslation('common')
  return (key: string, label: string, opts: HeaderOptions = {}): ReactElement => (
    <ColumnSortTh
      key={key}
      sortKey={key}
      label={label}
      align={opts.align ?? 'left'}
      active={sort.key === key}
      dir={sort.dir}
      onSort={onSort}
      ariaLabel={t('table.sortBy', { col: label })}
      {...(opts.className ? { className: opts.className } : {})}
      {...(opts.caps === false ? { caps: false } : {})}
    />
  )
}

export function useTableSort<T>(
  items: readonly T[] | null | undefined,
  accessors: SortAccessors<T>,
  options: TableSortOptions = {}
): {
  rows: T[]
  sort: SortState
  onSort: (key: string) => void
  header: (key: string, label: string, opts?: HeaderOptions) => ReactElement
} {
  const { i18n } = useTranslation('common')
  const { sort, onSort } = useSortState(options)

  // Las funciones de las columnas suelen declararse en línea y cambian de identidad en cada pintado: no deben re-ordenar solas.
  const accessorsRef = useRef(accessors)
  accessorsRef.current = accessors

  const locale = i18n.language?.slice(0, 2) || 'es'
  const rows = useMemo(
    () => sortRows(items ?? [], accessorsRef.current, sort.key, sort.dir, locale),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, sort.key, sort.dir, locale, ...(options.deps ?? [])]
  )

  return { rows, sort, onSort, header: useHeader(sort, onSort) }
}

/**
 * Orden por columna de una tabla PAGINADA EN EL SERVIDOR: el mismo estado y las mismas cabeceras que `useTableSort`, pero el
 * orden lo hace la API. Quien lo usa añade `sortParams` (`{ sort, dir }`, vacío si no hay orden) a la petición, vuelve a la
 * primera página al cambiar de orden y vuelve a pedir los datos cuando `sort.key` o `sort.dir` cambian.
 */
export function useServerTableSort(options: TableSortOptions = {}): {
  sort: SortState
  sortParams: { sort?: string; dir?: SortDir }
  onSort: (key: string) => void
  header: (key: string, label: string, opts?: HeaderOptions) => ReactElement
} {
  const { sort, onSort } = useSortState(options)
  const sortParams = sort.key ? { sort: sort.key, dir: sort.dir } : {}
  return { sort, sortParams, onSort, header: useHeader(sort, onSort) }
}
